/**
 * Browser telemetry for Stacks.
 *
 * The collector's OTLP endpoint does not answer CORS preflights, so the
 * browser posts to backend-phoenix's `/telemetry/v1/*` proxy instead. Set
 * VUE_APP_OTEL_EXPORTER_OTLP_ENDPOINT to that proxy, e.g.
 * `https://<phoenix-host>/telemetry`.
 *
 * Everything is opt-in: with no endpoint configured this module is a no-op.
 */

const rawEndpoint = process.env.VUE_APP_OTEL_EXPORTER_OTLP_ENDPOINT || ''
const endpoint = rawEndpoint.replace(/\/$/, '')
const serviceName = process.env.VUE_APP_OTEL_SERVICE_NAME || 'stacks-frontend'
const serviceNamespace = process.env.VUE_APP_OTEL_SERVICE_NAMESPACE || 'stacks'
const serviceVersion = process.env.VUE_APP_OTEL_SERVICE_VERSION || 'dev'
const environment = process.env.VUE_APP_OTEL_ENVIRONMENT || process.env.NODE_ENV || 'development'
// On by default whenever telemetry is enabled; set to "false" if a backend
// rejects the traceparent header.
const propagate = process.env.VUE_APP_OTEL_PROPAGATE !== 'false'

const enabled = Boolean(endpoint) && typeof window !== 'undefined'

const SEVERITY = {
  debug: { number: 5, text: 'DEBUG' },
  info: { number: 9, text: 'INFO' },
  warn: { number: 13, text: 'WARN' },
  error: { number: 17, text: 'ERROR' },
}

let queue = []
let flushTimer = null
let listenersInstalled = false

function randomHex(bytes) {
  const values = new Uint8Array(bytes)
  if (window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(values)
  } else {
    for (let i = 0; i < values.length; i += 1) {
      values[i] = Math.floor(Math.random() * 256)
    }
  }
  return Array.from(values, (value) => value.toString(16).padStart(2, '0')).join('')
}

function nowNano() {
  return String(Math.round(Date.now() * 1e6))
}

function attr(key, value) {
  let encoded
  if (typeof value === 'boolean') {
    encoded = { boolValue: value }
  } else if (typeof value === 'number' && Number.isInteger(value)) {
    encoded = { intValue: String(value) }
  } else if (typeof value === 'number') {
    encoded = { doubleValue: value }
  } else {
    encoded = { stringValue: String(value) }
  }
  return { key, value: encoded }
}

function attributesToOtlp(attributes) {
  return Object.entries(attributes || {})
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([key, value]) => attr(key, value))
}

function resourceAttributes() {
  return [
    attr('service.name', serviceName),
    attr('service.version', serviceVersion),
    attr('deployment.environment', environment),
    attr('deployment.environment.name', environment),
    attr('service.namespace', serviceNamespace),
    attr('browser.user_agent', navigator.userAgent || ''),
    attr('browser.url', window.location.href),
  ]
}

function errorAttributes(error) {
  if (!error) {
    return {}
  }
  const normalised = error instanceof Error ? error : new Error(String(error))
  return {
    'exception.type': normalised.name,
    'exception.message': normalised.message,
    'exception.stacktrace': normalised.stack || '',
  }
}

function send(signal, payload) {
  if (!enabled) {
    return
  }
  try {
    fetch(`${endpoint}/v1/${signal}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {})
  } catch (error) {
    // Telemetry must never break the application.
  }
}

export function flush() {
  if (!queue.length) {
    return
  }
  const pending = queue
  queue = []
  pending.forEach(({ signal, payload }) => send(signal, payload))
}

function enqueue(signal, payload) {
  if (!enabled) {
    return
  }
  queue.push({ signal, payload })
  if (queue.length >= 25) {
    flush()
    return
  }
  if (!flushTimer) {
    flushTimer = window.setInterval(flush, 5000)
  }
}

function logPayload(level, message, attributes, traceId, spanId) {
  const severity = SEVERITY[level] || SEVERITY.info
  const record = {
    timeUnixNano: nowNano(),
    severityNumber: severity.number,
    severityText: severity.text,
    body: { stringValue: String(message) },
    attributes: attributesToOtlp(attributes),
  }
  if (traceId) {
    record.traceId = traceId
  }
  if (spanId) {
    record.spanId = spanId
  }

  return {
    resourceLogs: [
      {
        resource: { attributes: resourceAttributes() },
        scopeLogs: [
          {
            scope: { name: 'stacks-frontend', version: serviceVersion },
            logRecords: [record],
          },
        ],
      },
    ],
  }
}

export function emitLog(level, message, options = {}) {
  if (!enabled) {
    return
  }
  const attributes = { ...(options.attributes || {}), ...errorAttributes(options.error) }
  enqueue('logs', logPayload(level, message, attributes, options.traceId, options.spanId))
}

export function captureException(error, attributes = {}) {
  const message = error && error.message ? error.message : String(error)
  emitLog('error', message, { attributes, error })
}

/**
 * Creates a span and returns a handle. Call `end()` exactly once; it emits the
 * span and, when `error` is supplied, a correlated error log.
 */
export function startSpan(name, attributes = {}, kind = 1) {
  const traceId = randomHex(16)
  const spanId = randomHex(8)
  const start = nowNano()
  let ended = false

  return {
    traceId,
    spanId,
    traceparent: `00-${traceId}-${spanId}-01`,
    end(extraAttributes = {}, error = null) {
      if (!enabled || ended) {
        return
      }
      ended = true

      const spanAttributes = { ...attributes, ...extraAttributes, ...errorAttributes(error) }
      enqueue('traces', {
        resourceSpans: [
          {
            resource: { attributes: resourceAttributes() },
            scopeSpans: [
              {
                scope: { name: 'stacks-frontend', version: serviceVersion },
                spans: [
                  {
                    traceId,
                    spanId,
                    name,
                    kind,
                    startTimeUnixNano: start,
                    endTimeUnixNano: nowNano(),
                    attributes: attributesToOtlp(spanAttributes),
                    status: { code: error ? 2 : 0 },
                  },
                ],
              },
            ],
          },
        ],
      })

      if (error) {
        emitLog('error', error.message || String(error), {
          attributes: spanAttributes,
          error,
          traceId,
          spanId,
        })
      }
    },
  }
}

export function shouldPropagate() {
  return enabled && propagate
}

export function initTelemetry(app) {
  if (!enabled || listenersInstalled) {
    return
  }
  listenersInstalled = true

  window.addEventListener('error', (event) => {
    const message = event.message || 'Unhandled browser error'
    captureException(event.error || new Error(message), {
      'error.source': event.filename || '',
      'error.line': event.lineno || 0,
      'error.column': event.colno || 0,
    })
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason
    captureException(reason instanceof Error ? reason : new Error(String(reason)), {
      'error.source': 'unhandledrejection',
    })
  })

  window.addEventListener('pagehide', flush)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush()
    }
  })

  if (app && app.config) {
    const previousHandler = app.config.errorHandler
    app.config.errorHandler = (error, instance, info) => {
      captureException(error, { 'vue.error_info': info || '' })
      if (previousHandler) {
        previousHandler(error, instance, info)
      } else {
        console.error(error)
      }
    }
  }
}
